// A workflow's timetable and its end-of-run message, as the Run settings
// card edits them (pure; scheduleModel.selftest.ts runs it):
//
//   presets      "Every weekday at 07:00", "Every day at…", "Every Monday
//                at…", "On the 1st at…", or a cron typed by hand; a saved
//                cron is read back into the preset it fits, else Custom
//   zones        a short list the dropdown offers (the browser's own first)
//   words        "Weekdays at 07:00 · next Mon 5 Oct, 07:00"
//   problems     what the server would refuse: a cron it cannot read, a
//                scheduled run missing a required input, a bad address
//
// Cron hours are evaluated in the schedule's zone by the server; the
// browser's `nextRun` evaluates them in local time, so the next fire is
// labelled with the zone when it is not the browser's.

import type { FinishEmail, ParameterSpec, ScheduleSpec, WorkflowSettings } from "@/api/workflows";
import { humanizeCron, nextRun, parseCron } from "@/lib/cron";
import { formatDateTime, type DateInput } from "@/lib/format";

import { initialRunForm, runFormProblems, runParamsBody, type RunForm } from "../model";

export type SchedulePreset = "weekdays" | "daily" | "weekly" | "monthly" | "custom";

export interface ScheduleDraft {
  preset: SchedulePreset;
  /** "HH:MM" (24-hour) for every preset but custom. */
  time: string;
  /** 0 = Sunday … 6 = Saturday, for the weekly preset. */
  weekday: number;
  /** The expression as typed, for the custom preset. */
  cron: string;
}

export const PRESETS: { value: SchedulePreset; label: string }[] = [
  { value: "weekdays", label: "Every weekday" },
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "monthly", label: "Every month, on the 1st" },
  { value: "custom", label: "Custom (cron)" },
];

export const WEEKDAY_OPTIONS: { value: string; label: string }[] = [
  ["1", "Monday"], ["2", "Tuesday"], ["3", "Wednesday"], ["4", "Thursday"], ["5", "Friday"], ["6", "Saturday"], ["0", "Sunday"],
].map(([value, label]) => ({ value, label }));

export const DEFAULT_TZ = "Europe/London";
export const DEFAULT_SCHEDULE: ScheduleSpec = { enabled: true, cron: "0 7 * * 1-5", tz: DEFAULT_TZ, params: {} };

const COMMON_ZONES = ["Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Zurich", "America/New_York", "America/Chicago", "America/Los_Angeles", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Australia/Sydney", "UTC"];

/** The browser's own zone ("" when it cannot say). */
export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

/** The zones the dropdown offers: the browser's, the one set, then the common list, no repeats. */
export function zoneOptions(current: string, browser = browserZone()): string[] {
  const out: string[] = [];
  for (const z of [browser, current, ...COMMON_ZONES]) if (z && !out.includes(z)) out.push(z);
  return out;
}

/** "Europe/London" → "London"; "America/New_York" → "New York". */
export function zoneLabel(zone: string): string {
  const city = zone.split("/").pop() ?? zone;
  return city.replace(/_/g, " ");
}

const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function timeParts(time: string): { h: number; m: number } | null {
  const m = TIME_RE.exec(time.trim());
  return m ? { h: Number(m[1]), m: Number(m[2]) } : null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The cron an edited draft stands for; null when its time (or custom text) is not usable yet. */
export function cronFromDraft(d: ScheduleDraft): string | null {
  if (d.preset === "custom") return parseCron(d.cron) ? d.cron.trim().replace(/\s+/g, " ") : null;
  const t = timeParts(d.time);
  if (!t) return null;
  const at = `${t.m} ${t.h}`;
  switch (d.preset) {
    case "weekdays":
      return `${at} * * 1-5`;
    case "daily":
      return `${at} * * *`;
    case "weekly":
      return `${at} * * ${d.weekday}`;
    case "monthly":
      return `${at} 1 * *`;
  }
}

/** A saved cron read back into the preset it fits (at one minute of one hour), else Custom with the text. */
export function draftFromCron(cron: string): ScheduleDraft {
  const custom: ScheduleDraft = { preset: "custom", time: "07:00", weekday: 1, cron };
  const spec = parseCron(cron);
  if (!spec || spec.minutes.length !== 1 || spec.hours.length !== 1 || spec.months.length !== 12) return custom;
  const time = `${pad(spec.hours[0])}:${pad(spec.minutes[0])}`;
  const everyDom = spec.dom.length === 31;
  const everyDow = spec.dow.length === 7;
  if (everyDom && everyDow) return { preset: "daily", time, weekday: 1, cron };
  if (everyDom && spec.dow.length === 5 && [1, 2, 3, 4, 5].every((d) => spec.dow.includes(d))) return { preset: "weekdays", time, weekday: 1, cron };
  if (everyDom && spec.dow.length === 1) return { preset: "weekly", time, weekday: spec.dow[0], cron };
  if (everyDow && spec.dom.length === 1 && spec.dom[0] === 1) return { preset: "monthly", time, weekday: 1, cron };
  return custom;
}

/** "Weekdays at 07:00" / "Daily at 18:30" / the raw cron when it cannot be said in words. */
export function cronWords(cron: string): string {
  return humanizeCron(cron) ?? cron;
}

/** "next Mon 5 Oct, 07:00" (with the zone when it is not the browser's); "" when the cron is unreadable. */
export function nextWords(cron: string, tz: string, now?: DateInput, browser = browserZone()): string {
  const next = nextRun(cron, now);
  if (!next) return "";
  const when = formatDateTime(next, { relativeDay: true, inline: true }, now);
  return tz && browser && tz !== browser ? `next ${when} (${zoneLabel(tz)})` : `next ${when}`;
}

/** The card's line for the timetable: "Weekdays 07:00 · next Mon 5 Oct, 07:00", "Off" when none. */
export function scheduleLine(schedule: ScheduleSpec | null | undefined, now?: DateInput, browser = browserZone()): string {
  if (!schedule || !schedule.enabled) return "";
  const next = nextWords(schedule.cron, schedule.tz, now, browser);
  return [cronWords(schedule.cron), next].filter(Boolean).join(" · ");
}

// ─── the scheduled run's inputs ───

/** The form the schedule's parameters are edited through (the Run dialog's shape). */
export function scheduleForm(params: readonly ParameterSpec[], schedule: ScheduleSpec | null | undefined): RunForm {
  return initialRunForm(params, schedule?.params ?? undefined);
}

/**
 * The schedule's `params` from its form: typed values, empty optional ones
 * left out, and a value the form still flags ("1." half-typed in a number
 * box) left out too, so the spec never carries a NaN.
 */
export function scheduleParams(params: readonly ParameterSpec[], form: RunForm): Record<string, unknown> {
  const problems = runFormProblems(params, form);
  return runParamsBody(params.filter((p) => !problems[p.name]), form);
}

// ─── problems ───

// What the server would refuse lives with the other rules (rules.ts:
// scheduleIssues, emailProblem), so the editor's "to fix" list has them too.
export { emailProblem, scheduleIssues } from "../rules";

/** Problems per parameter of the scheduled run's form (the Run dialog's checks). */
export function scheduleFormProblems(params: readonly ParameterSpec[], form: RunForm): Record<string, string> {
  return runFormProblems(params, form);
}

// ─── the card's line ───

/** "Weekdays at 07:00 · 4 at once · 45 min per step · chat + email". */
export function settingsLine(settings: WorkflowSettings, schedule: ScheduleSpec | null | undefined, workspaceCap: number): string {
  const when = schedule?.enabled ? cronWords(schedule.cron) : "";
  const tells = [settings.notify === "telegram" ? "chat" : "", settings.email ? "email" : ""].filter(Boolean);
  return [when, `${Math.min(settings.max_parallel, workspaceCap)} at once`, `${settings.default_stage_minutes} min per step`, tells.length ? tells.join(" + ") : "no message"].filter(Boolean).join(" · ");
}

/** The default subject the server uses when none is typed. */
export function defaultSubject(name: string): string {
  return `${name.trim() || "Workflow"} — finished: <conclusion>`;
}

/** A FinishEmail switched on with nothing set yet. */
export function emptyEmail(): FinishEmail {
  return { to: [], subject: "", attach: true, only_on_success: false };
}

export { nextRun };
